/**
 * Minimal GTwinClient stub for `app-geoviewer`.
 *
 * Exposes only the methods the viewer actually calls:
 *   - `client.get(id)` — fetch a gTwin by id (throws NotFoundError on 404).
 *   - `client.scene.presets.activate(gtwinId, presetId)` — PATCH merged payload
 *     (`activeScenePreset`) via `PATCH /api/v1/gtwins/{id}` + version gate.
 *   - `client.projectAnchor.set(gtwinId, anchor)` — same PATCH surface for
 *     `envelope.projectAnchor`.
 *
 * This is a strict subset of `app-platform-gtwin/packages/sdk-ts/src/index.ts`
 * (shape enforced by `__tests__/drift.test.ts`).
 *
 * In tests, swap `fetchImpl` for a vitest mock so no real HTTP is sent.
 */

import type { ProjectAnchor, SceneItem, ScenePreset, GTwinRecord, EnvelopeView } from "./types.js";
import { NotFoundError } from "./errors.js";
import type {
  PackageAdapter,
  PackageExportResult,
  SceneItemInput,
  SceneItemPatch,
  SceneItemsAdapter,
  SceneItemsCopyFromInput,
  SceneItemsCopyFromResult,
  ScenePresetInput,
  ScenePresetsAdapter,
} from "./scene-items.js";
import {
  getSceneItemsAdapter,
  type SceneItemsMode,
  type SceneItemsSurface,
} from "./sceneItemsAdapter.js";
import type { RealSceneItemsAdapterOptions } from "./sceneItemsReal.js";

export interface GTwinClientOptions {
  baseUrl: string;
  getToken?: () => Promise<string | undefined>;
  defaultTenantId?: string;
  fetchImpl?: typeof fetch;
  /**
   * Optional override for the scene-items mutation surface. When omitted the
   * adapter is resolved via `getSceneItemsAdapter()` (reads
   * `VITE_GTWIN_SCENE_ITEMS=mock|real`, default `mock`). Tests may inject a
   * custom adapter to exercise both branches.
   */
  sceneItemsSurface?: SceneItemsSurface;
  /** Convenience override; if provided, `sceneItemsSurface` is computed via `getSceneItemsAdapter(mode)`. */
  sceneItemsMode?: SceneItemsMode;
  /**
   * Optional one-shot retry callback fired when a `real`-mode mutation
   * rejects with `409 stale-parent-version`. Mirrors the platform SDK's
   * `StaleVersionRetry`; receives the freshly-loaded envelope and
   * returns the new `parentVersion` to retry once (or `undefined` to
   * surface the original `StaleParentVersionError`).
   */
  onStaleParent?: RealSceneItemsAdapterOptions["onStaleParent"];
}

function cloneJson<T>(value: T): T {
  return typeof structuredClone === "function"
    ? structuredClone(value)
    : (JSON.parse(JSON.stringify(value)) as T);
}

// ---------------------------------------------------------------------------
// PresetsNamespace — activate (PATCH) + upsert / remove (delegated to the
// WP-1 adapter; mock by default until the real platform endpoints land).
// ---------------------------------------------------------------------------

class PresetsNamespace {
  constructor(
    private readonly _fetch: typeof fetch,
    private readonly _base: URL,
    private readonly _getToken: GTwinClientOptions["getToken"],
    private readonly _tenant: string,
    private readonly _getTwin: (id: string) => Promise<GTwinRecord>,
    private readonly _adapter: ScenePresetsAdapter,
  ) {}

  /**
   * Persist active scene preset. Server has no `/preset` sub-resource; the
   * gTwin API expects `PATCH /api/v1/gtwins/{id}` with `parentVersion`,
   * `updatedBy`, and merged `envelope` + `payload` (mirrors sdk-py
   * read-modify-write).
   */
  async activate(gtwinId: string, presetId: string): Promise<void> {
    const row = await this._getTwin(gtwinId);
    const envelope = cloneJson(row.envelope) as unknown as Record<string, unknown>;
    const payload = cloneJson(
      (row.payload ?? {}) as Record<string, unknown>,
    ) as Record<string, unknown>;
    const geospatial = {
      ...((payload.geospatial as Record<string, unknown> | undefined) ?? {}),
      activeScenePreset: presetId,
    };
    payload.geospatial = geospatial;

    const r = await this._fetch(
      new URL(`api/v1/gtwins/${encodeURIComponent(gtwinId)}`, this._base),
      {
        method: "PATCH",
        headers: await this._headers(),
        body: JSON.stringify({
          parentVersion: row.version,
          updatedBy: "geoviewer",
          envelope,
          payload,
        }),
      },
    );
    if (r.status === 404) throw new NotFoundError(`gTwin ${gtwinId} not found`);
    if (!r.ok) throw new Error(`activate failed: ${r.status} ${await r.text()}`);
  }

  /**
   * Upsert a named scene preset (visibility set, opacities, camera, time).
   * Today this routes through the WP-1 adapter (mock by default). Once the
   * platform `PUT /api/v1/gtwins/{id}/scene/presets/{presetId}` endpoint
   * lands (`SCENE_ITEMS_SDK_SPEC.md` §2.3), `mode: "real"` will hit it
   * directly.
   */
  async upsert(gtwinId: string, preset: ScenePresetInput): Promise<ScenePresetInput> {
    return this._adapter.upsert(gtwinId, preset);
  }

  /** Remove a named scene preset. Routed through the WP-1 adapter. */
  async remove(gtwinId: string, presetId: string): Promise<void> {
    await this._adapter.remove(gtwinId, presetId);
  }

  private async _headers(): Promise<Record<string, string>> {
    const t = await this._getToken?.();
    return {
      "content-type": "application/json",
      "x-tenant-id": this._tenant,
      ...(t ? { authorization: `Bearer ${t}` } : {}),
    };
  }
}

// ---------------------------------------------------------------------------
// SceneItemsNamespace — WP-1 mutation surface.
//
// All four methods route through the `SceneItemsAdapter` resolved by the
// `VITE_GTWIN_SCENE_ITEMS` flag (default `mock`). Consumer code compiles
// against this surface today; the real platform implementation will be a
// drop-in replacement for the adapter once Track A ships.
// ---------------------------------------------------------------------------

export class SceneItemsNamespace {
  constructor(private readonly _adapter: SceneItemsAdapter) {}

  add(gtwinId: string, item: SceneItemInput): Promise<SceneItem> {
    return this._adapter.add(gtwinId, item);
  }

  remove(gtwinId: string, itemId: string): Promise<void> {
    return this._adapter.remove(gtwinId, itemId);
  }

  patch(
    gtwinId: string,
    patches: ReadonlyArray<SceneItemPatch>,
  ): Promise<ReadonlyArray<SceneItem>> {
    return this._adapter.patch(gtwinId, patches);
  }

  reorder(gtwinId: string, layerOrder: ReadonlyArray<string>): Promise<void> {
    return this._adapter.reorder(gtwinId, layerOrder);
  }

  copyFrom(
    targetGtwinId: string,
    input: SceneItemsCopyFromInput,
  ): Promise<SceneItemsCopyFromResult> {
    return this._adapter.copyFrom(targetGtwinId, input);
  }
}

// ---------------------------------------------------------------------------
// PackageNamespace — WP-1 export surface.
// ---------------------------------------------------------------------------

export class PackageNamespace {
  constructor(private readonly _adapter: PackageAdapter) {}

  /**
   * Build a `.gtwin` archive (thin by default) and return the download URL +
   * checksum + size. Wraps the WP-1 adapter so the same call works for both
   * the in-memory mock and the future server endpoint.
   */
  exportGtwin(
    gtwinId: string,
    options?: {
      mode?: "thin" | "fat";
      onProgress?: (received: number, total: number | null) => void;
    },
  ): Promise<PackageExportResult> {
    return this._adapter.exportGtwin(gtwinId, options);
  }
}

// ---------------------------------------------------------------------------
// SceneNamespace — exposes presets + items.
// ---------------------------------------------------------------------------

export class SceneNamespace {
  readonly presets: PresetsNamespace;
  readonly items: SceneItemsNamespace;

  constructor(options: {
    fetchImpl: typeof fetch;
    base: URL;
    getToken: GTwinClientOptions["getToken"];
    tenant: string;
    getTwin: (id: string) => Promise<GTwinRecord>;
    surface: SceneItemsSurface;
  }) {
    this.presets = new PresetsNamespace(
      options.fetchImpl,
      options.base,
      options.getToken,
      options.tenant,
      options.getTwin,
      options.surface.presets,
    );
    this.items = new SceneItemsNamespace(options.surface.items);
  }
}

// ---------------------------------------------------------------------------
// ProjectAnchorNamespace
// ---------------------------------------------------------------------------

export class ProjectAnchorNamespace {
  constructor(
    private readonly _fetch: typeof fetch,
    private readonly _base: URL,
    private readonly _getToken: GTwinClientOptions["getToken"],
    private readonly _tenant: string,
    private readonly _getTwin: (id: string) => Promise<GTwinRecord>,
  ) {}

  async set(gtwinId: string, anchor: ProjectAnchor): Promise<void> {
    const row = await this._getTwin(gtwinId);
    const envelope = cloneJson(row.envelope) as unknown as Record<string, unknown>;
    envelope.projectAnchor = anchor as unknown as Record<string, unknown>;
    const payload = cloneJson(
      (row.payload ?? {}) as Record<string, unknown>,
    ) as Record<string, unknown>;

    const r = await this._fetch(
      new URL(`api/v1/gtwins/${encodeURIComponent(gtwinId)}`, this._base),
      {
        method: "PATCH",
        headers: await this._headers(),
        body: JSON.stringify({
          parentVersion: row.version,
          updatedBy: "geoviewer",
          envelope,
          payload,
        }),
      },
    );
    if (r.status === 404) throw new NotFoundError(`gTwin ${gtwinId} not found`);
    if (!r.ok) throw new Error(`set anchor failed: ${r.status} ${await r.text()}`);
  }

  private async _headers(): Promise<Record<string, string>> {
    const t = await this._getToken?.();
    return {
      "content-type": "application/json",
      "x-tenant-id": this._tenant,
      ...(t ? { authorization: `Bearer ${t}` } : {}),
    };
  }
}

// ---------------------------------------------------------------------------
// ViewNamespace — persist camera + saved views on envelope.view
// ---------------------------------------------------------------------------

export class ViewNamespace {
  constructor(
    private readonly _fetch: typeof fetch,
    private readonly _base: URL,
    private readonly _getToken: GTwinClientOptions["getToken"],
    private readonly _tenant: string,
    private readonly _getTwin: (id: string) => Promise<GTwinRecord>,
  ) {}

  async save(gtwinId: string, view: EnvelopeView): Promise<void> {
    const row = await this._getTwin(gtwinId);
    const envelope = cloneJson(row.envelope) as unknown as Record<string, unknown>;
    envelope.view = view as unknown as Record<string, unknown>;
    const payload = cloneJson(
      (row.payload ?? {}) as Record<string, unknown>,
    ) as Record<string, unknown>;

    const r = await this._fetch(
      new URL(`api/v1/gtwins/${encodeURIComponent(gtwinId)}`, this._base),
      {
        method: "PATCH",
        headers: await this._headers(),
        body: JSON.stringify({
          parentVersion: row.version,
          updatedBy: "geoviewer",
          envelope,
          payload,
        }),
      },
    );
    if (r.status === 404) throw new NotFoundError(`gTwin ${gtwinId} not found`);
    if (!r.ok) throw new Error(`save view failed: ${r.status} ${await r.text()}`);
  }

  private async _headers(): Promise<Record<string, string>> {
    const t = await this._getToken?.();
    return {
      "content-type": "application/json",
      "x-tenant-id": this._tenant,
      ...(t ? { authorization: `Bearer ${t}` } : {}),
    };
  }
}

// ---------------------------------------------------------------------------
// GTwinClient
// ---------------------------------------------------------------------------

export class GTwinClient {
  private readonly _base: URL;
  private readonly _getToken: GTwinClientOptions["getToken"];
  private readonly _tenant: string;
  private readonly _fetch: typeof fetch;
  private readonly _sceneItemsSurface: SceneItemsSurface;

  readonly scene: SceneNamespace;
  readonly projectAnchor: ProjectAnchorNamespace;
  readonly package: PackageNamespace;
  readonly view: ViewNamespace;

  constructor(options: GTwinClientOptions) {
    this._base = new URL(options.baseUrl);
    this._getToken = options.getToken;
    this._tenant = options.defaultTenantId ?? "dev-tenant";
    // Bind to globalThis so the browser's `fetch` keeps its `this` context
    // when called via the stored reference (otherwise: Illegal invocation).
    this._fetch = options.fetchImpl ?? ((...args) => fetch(...args));
    this._sceneItemsSurface =
      options.sceneItemsSurface ??
      getSceneItemsAdapter(options.sceneItemsMode, {
        baseUrl: this._base.toString(),
        fetchImpl: this._fetch,
        getToken: this._getToken,
        defaultTenantId: this._tenant,
        onStaleParent: options.onStaleParent,
      });

    const getTwin = (id: string) => this.get(id);
    this.scene = new SceneNamespace({
      fetchImpl: this._fetch,
      base: this._base,
      getToken: this._getToken,
      tenant: this._tenant,
      getTwin,
      surface: this._sceneItemsSurface,
    });
    this.projectAnchor = new ProjectAnchorNamespace(
      this._fetch,
      this._base,
      this._getToken,
      this._tenant,
      getTwin,
    );
    this.view = new ViewNamespace(
      this._fetch,
      this._base,
      this._getToken,
      this._tenant,
      getTwin,
    );
    this.package = new PackageNamespace(this._sceneItemsSurface.package);
  }

  /** The active scene-items surface mode (`"mock"` or `"real"`). */
  get sceneItemsMode(): SceneItemsMode {
    return this._sceneItemsSurface.mode;
  }

  async get(id: string): Promise<GTwinRecord> {
    const t = await this._getToken?.();
    const r = await this._fetch(
      new URL(`api/v1/gtwins/${encodeURIComponent(id)}`, this._base),
      {
        method: "GET",
        headers: {
          "content-type": "application/json",
          "x-tenant-id": this._tenant,
          ...(t ? { authorization: `Bearer ${t}` } : {}),
        },
      },
    );
    if (r.status === 404) throw new NotFoundError(`gTwin ${id} not found`);
    if (!r.ok) throw new Error(`get failed: ${r.status} ${await r.text()}`);
    return (await r.json()) as GTwinRecord;
  }
}

// Re-export default presets so route code can reference canonical ids
export const DEFAULT_PRESETS: readonly ScenePreset[] = [
  {
    id: "scn-map-only",
    label: "Map only",
    show: ["basemaps", "terrain", "featureLayers"],
  },
  {
    id: "scn-twin-only",
    label: "gTwin / models only",
    // `realityData` is where 3D Tiles city models, reality meshes, and
    // photogrammetry scans live (RFC-108 scene-item taxonomy). They are
    // first-class "twin" content; omitting the slot here causes the
    // "Twin / models only" preset to filter the entire Tokyo passthrough
    // dataset (and every photoreal city model going forward).
    show: ["realityData", "engineeringModels", "terrain"],
  },
  {
    id: "scn-combined",
    label: "Combined",
    show: ["basemaps", "terrain", "realityData", "engineeringModels", "featureLayers", "overlays"],
  },
] as const;
